import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CloudUploadRounded from '@mui/icons-material/CloudUploadRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { uploadImage } from '../../services/uploadsApi';

// Single-image uploader. `value` is the image URL (or empty). `onChange(url)`
// fires when an upload succeeds or the image is cleared. `folder` is the
// logical GCS prefix (e.g. "fabric-colors", "finish-colors", "fabric-coatings").
const ImageUploader = ({ value, onChange, folder = 'uploads', label = 'Image' }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handlePick = () => inputRef.current?.click();

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file');
      return;
    }
    setError('');
    setUploading(true);
    try {
      const url = await uploadImage(file, folder);
      onChange(url);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>{label}</Typography>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      {value ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              position: 'relative', width: 80, height: 80,
              borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Box component="img" src={value} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <IconButton
              size="small"
              onClick={() => onChange('')}
              sx={{
                position: 'absolute', top: 2, right: 2,
                bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', p: 0.25,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <CloseRounded sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
          <Button size="small" onClick={handlePick} disabled={uploading} startIcon={uploading ? <CircularProgress size={14} /> : <CloudUploadRounded />}>
            {uploading ? 'Uploading…' : 'Replace'}
          </Button>
        </Box>
      ) : (
        <Button
          variant="outlined"
          onClick={handlePick}
          disabled={uploading}
          startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadRounded />}
        >
          {uploading ? 'Uploading…' : 'Choose Image'}
        </Button>
      )}
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{error}</Typography>
      )}
    </Box>
  );
};

export default ImageUploader;
